/**
 * ProjectSwitcher
 *
 * Header pill that switches between the demo projects (Snowdon
 * Towers / Casita Blanca). On change, the projectStore updates and
 * the App/FormaApp effects re-fire all loaders (engine, properties,
 * viewer default sheet) against the new project's URLs. The
 * Requirements Intelligence panel, jurisdiction picker, sheet
 * picker, and violations panel all subscribe to either the store
 * or to the loaders' outputs, so they update without explicit
 * wiring here.
 *
 * Two visual variants:
 *   - "dark"  → standalone Vera header (matches UiModeToggle style).
 *   - "light" → Forma sub-bar (matches the project-crumb chrome).
 *
 * Subtitle under the pill shows the project's city + code basis so
 * the user can see at a glance which jurisdiction is implied.
 */
import { PROJECTS } from "../data/projects";
import { useCurrentProject, useProjectStore } from "../stores/projectStore";

export function ProjectSwitcher({
  variant = "dark",
}: {
  variant?: "dark" | "light";
}) {
  const current = useCurrentProject();
  const setCurrent = useProjectStore(s => s.setCurrentProject);
  const isLight = variant === "light";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        lineHeight: 1.15,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span
          style={{
            color: isLight ? "#4b5563" : "var(--fc2-text-dim)",
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Project
        </span>
        <div
          role="tablist"
          aria-label="Switch demo project"
          style={{
            display: "inline-flex",
            background: isLight ? "#f2f3f5" : "rgba(255,255,255,0.04)",
            border: `1px solid ${isLight ? "#d8dde3" : "var(--fc2-border)"}`,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {PROJECTS.map(p => {
            const active = p.id === current.id;
            const activeBg = isLight ? "#0a72e8" : "rgba(127,179,213,0.18)";
            const activeFg = isLight ? "#ffffff" : "#7fb3d5";
            const idleFg = isLight ? "#374151" : "var(--fc2-text)";
            return (
              <button
                type="button"
                key={p.id}
                role="tab"
                aria-selected={active}
                onClick={() => setCurrent(p.id)}
                title={`${p.location.city}, ${p.location.stateCode} — ${p.codeBasis}`}
                style={{
                  background: active ? activeBg : "transparent",
                  color: active ? activeFg : idleFg,
                  border: "none",
                  padding: "4px 10px",
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: 11,
                  textTransform: "none",
                  letterSpacing: 0.2,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {projectShortName(p.id)}
              </button>
            );
          })}
        </div>
      </div>
      <span
        style={{
          fontSize: 10,
          color: isLight ? "#6b7280" : "var(--fc2-text-dim)",
          maxWidth: 360,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={`${current.location.city}, ${current.location.stateCode} · ${current.codeBasis}`}
      >
        {current.location.city}, {current.location.stateCode} ·{" "}
        {current.codeBasis}
      </span>
    </div>
  );
}

/**
 * Friendly short name for the pill — the full project name is too
 * long to fit comfortably (e.g. "Snowdon Towers — Mixed-Use Permit
 * Package"), so we hand-pick a short label per id.
 */
function projectShortName(id: string): string {
  switch (id) {
    case "snowdon-towers":
      return "Sustainable Villa (NYC)";
    case "casita-blanca":
      return "Casita Blanca (LA)";
    default:
      return id;
  }
}
